/**
 * Compact timeline node when a linked meeting doc is moved or created.
 */
import { FilePlus2, FileText, FolderInput } from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'

export type InboxDocumentEventKind = 'moved' | 'created' | 'created_batch'

export type InboxDocumentEventProps = {
  event: InboxDocumentEventKind
  actorLabel: string
  at: string
  title: string
  documentId?: string | null
  oldPath?: string | null
  newPath?: string | null
  documents?: Array<{ id?: string; title?: string; path?: string }>
  onOpenDocument: (documentId: string) => void
  className?: string
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function heading(event: InboxDocumentEventKind): string {
  if (event === 'moved') return 'Filed'
  if (event === 'created_batch') return 'Created from this meeting'
  return 'Created'
}

export function InboxDocumentEvent({
  event,
  actorLabel,
  at,
  title,
  documentId,
  oldPath,
  newPath,
  documents,
  onOpenDocument,
  className,
}: InboxDocumentEventProps) {
  const path = (newPath || oldPath || '').trim()
  const filename = path.split('/').filter(Boolean).pop() || title
  const Icon = event === 'moved' ? FolderInput : FilePlus2
  const extras = (documents || []).filter((doc) => doc?.id || doc?.title)

  return (
    <div
      className={cn(
        'rounded-md border border-border/80 bg-muted/30 px-3 py-3',
        className,
      )}
      data-testid={testIds.inbox.documentEvent}
      data-event={event}
    >
      <div className="flex items-start gap-2.5">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-sm font-semibold text-foreground">{heading(event)}</span>
            <span className="text-xs text-muted-foreground">
              by {actorLabel} · {formatWhen(at)}
            </span>
          </div>
          {event === 'moved' ? (
            <p className="text-xs text-muted-foreground">
              Meeting notes moved out of the ingest folder.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Follow-up work captured from this meeting.
            </p>
          )}
          {event !== 'created_batch' && (documentId || path) ? (
            <button
              type="button"
              onClick={() => {
                const target = (documentId || path).trim()
                if (!target) return
                onOpenDocument(target)
              }}
              className="inline-flex max-w-full items-center gap-1.5 rounded border border-border bg-background/80 px-2 py-1 text-left text-xs font-medium text-primary hover:bg-muted"
              data-testid={testIds.inbox.documentEventDocLink}
              title={path}
            >
              <FileText className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{title || filename}</span>
            </button>
          ) : null}
          {extras.map((doc) => {
            const target = (doc.id || doc.path || '').trim()
            return (
              <button
                key={target || doc.title}
                type="button"
                onClick={() => {
                  if (!target) return
                  onOpenDocument(target)
                }}
                className="inline-flex max-w-full items-center gap-1.5 rounded border border-border bg-background/80 px-2 py-1 text-left text-xs font-medium text-primary hover:bg-muted"
              >
                <FileText className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{doc.title || doc.path}</span>
              </button>
            )
          })}
          {path && event !== 'created_batch' ? (
            <p className="truncate font-mono text-[10px] text-muted-foreground/80" title={path}>
              {path}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
